/**
 * InvitePage: full-screen overlay for adding a new accountant.
 *
 * Firm ID path now surfaces a disabled Save button in the dark footer when
 * the field has content (so the UI feels complete), plus a teal explainer
 * card telling the user what a firm ID is and where to get it.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useMyAccountant } from "../../../hooks/useMyAccountant";
import { isValidEmail } from "./myAccountantUtils";
import { PALETTE } from "./constants";

type Method = "firmId" | "email";

interface EmailDraft {
  firstName: string;
  lastName: string;
  email: string;
}

interface EmailErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
}

export default function InvitePage() {
  const navigate = useNavigate();
  const { invite } = useMyAccountant();
  const uid = useId().replace(/:/g, "");
  const styleTag = useInvitePageStyles(uid);

  const [method, setMethod] = useState<Method>("email");
  const [draft, setDraft] = useState<EmailDraft>({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [firmId, setFirmId] = useState<string>("");
  const [touched, setTouched] = useState<Record<keyof EmailDraft, boolean>>({
    firstName: false,
    lastName: false,
    email: false,
  });
  const [showAllErrors, setShowAllErrors] = useState(false);

  const errors = useMemo<EmailErrors>(() => {
    const e: EmailErrors = {};
    if (!draft.firstName.trim()) e.firstName = "First name is required.";
    if (!draft.lastName.trim()) e.lastName = "Last name is required.";
    if (!draft.email.trim()) {
      e.email = "Email is required.";
    } else if (!isValidEmail(draft.email)) {
      e.email = "Enter a valid email address.";
    }
    return e;
  }, [draft]);

  const emailIsValid = Object.keys(errors).length === 0;
  const emailAllFilled =
    !!draft.firstName.trim() &&
    !!draft.lastName.trim() &&
    !!draft.email.trim();
  const firmIdFilled = !!firmId.trim();

  // Footer visibility: when the active method has content.
  const showFooter =
    (method === "email" && emailAllFilled) ||
    (method === "firmId" && firmIdFilled);
  const saveDisabled = method === "firmId";

  const updateField = useCallback(
    (field: keyof EmailDraft) =>
      (e: ChangeEvent<HTMLInputElement>) => {
        setDraft((prev) => ({ ...prev, [field]: e.target.value }));
      },
    [],
  );

  const markTouched = useCallback(
    (field: keyof EmailDraft) => () => {
      setTouched((prev) => ({ ...prev, [field]: true }));
    },
    [],
  );

  const handleClose = useCallback(() => {
    navigate("/accounting/my-accountant");
  }, [navigate]);

  const handleCrumbClick = useCallback(
    (path: string) => (e: React.MouseEvent) => {
      e.preventDefault();
      navigate(path);
    },
    [navigate],
  );

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (method !== "email") return;
      if (!emailIsValid) {
        setShowAllErrors(true);
        setTouched({ firstName: true, lastName: true, email: true });
        return;
      }
      const fullName = `${draft.firstName.trim()} ${draft.lastName.trim()}`.trim();
      invite({
        email: draft.email.trim(),
        name: fullName,
        firmName: "",
      });
      navigate("/accounting/my-accountant", {
        state: { toast: "Invite sent" },
      });
    },
    [method, emailIsValid, draft, invite, navigate],
  );

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleClose]);

  const showErr = (field: keyof EmailDraft): boolean =>
    (touched[field] || showAllErrors) && !!errors[field];

  return (
    <>
      {styleTag}
      <div className={`ip-${uid}-page`}>
        <header className={`ip-${uid}-top`}>
          <div className={`ip-${uid}-top-stack`}>
            <nav className={`ip-${uid}-crumb`} aria-label="Breadcrumb">
              <a
                href="/accounting"
                onClick={handleCrumbClick("/accounting")}
                className={`ip-${uid}-crumb-link`}
              >
                Accounting
              </a>
              <span className={`ip-${uid}-crumb-sep`}>/</span>
              <a
                href="/accounting/my-accountant"
                onClick={handleCrumbClick("/accounting/my-accountant")}
                className={`ip-${uid}-crumb-link`}
              >
                My accountant
              </a>
              <span className={`ip-${uid}-crumb-sep`}>/</span>
              <strong className={`ip-${uid}-crumb-current`}>
                Add new accountant
              </strong>
            </nav>
            <h1 className={`ip-${uid}-top-title`}>Add a new accountant</h1>
          </div>
          <button
            type="button"
            className={`ip-${uid}-close`}
            onClick={handleClose}
            aria-label="Close"
          >
            <X size={20} strokeWidth={2} />
          </button>
        </header>

        <form className={`ip-${uid}-body`} onSubmit={handleSubmit} noValidate>
          <h2 className={`ip-${uid}-sub`}>
            Connect your accountant faster with firm ID
          </h2>
          <p className={`ip-${uid}-intro`}>
            Your accountant and members of their firm will have admin access to
            your company data. Invites expire after 30 days.
          </p>

          <div className={`ip-${uid}-ropt`}>
            <label className={`ip-${uid}-rhead`}>
              <input
                type="radio"
                name="method"
                value="firmId"
                checked={method === "firmId"}
                onChange={() => setMethod("firmId")}
                className={`ip-${uid}-radio`}
              />
              <span className={`ip-${uid}-rlabel`}>
                Enter my accountant's firm ID
              </span>
            </label>

            {method === "firmId" && (
              <div className={`ip-${uid}-rbody`}>
                <div className={`ip-${uid}-field-single`}>
                  <label
                    htmlFor={`ip-${uid}-firm`}
                    className={`ip-${uid}-flabel`}
                  >
                    Accountant's firm ID
                  </label>
                  <input
                    id={`ip-${uid}-firm`}
                    type="text"
                    className={`ip-${uid}-finput`}
                    value={firmId}
                    onChange={(e) => setFirmId(e.target.value)}
                    placeholder="e.g. NOV-12345"
                    autoFocus
                  />
                  <div className={`ip-${uid}-explain`}>
                    <strong>What is a firm ID?</strong> A unique code (like{" "}
                    <code>NOV-82410</code>) that identifies your accountant's
                    firm on Novala. Ask your accountant for theirs; they will
                    find it on their accountant dashboard once Novala for
                    Accountants is live.
                  </div>
                  <p className={`ip-${uid}-note`}>
                    Firm name and verification will appear here once validated.
                    Firm ID invites launch with Novala for Accountants; use the
                    email method to invite now.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className={`ip-${uid}-ropt`}>
            <label className={`ip-${uid}-rhead`}>
              <input
                type="radio"
                name="method"
                value="email"
                checked={method === "email"}
                onChange={() => setMethod("email")}
                className={`ip-${uid}-radio`}
              />
              <span className={`ip-${uid}-rlabel`}>
                Enter my accountant's email
              </span>
            </label>

            {method === "email" && (
              <div className={`ip-${uid}-rbody`}>
                <div className={`ip-${uid}-row3`}>
                  <div className={`ip-${uid}-field`}>
                    <label
                      htmlFor={`ip-${uid}-first`}
                      className={`ip-${uid}-flabel`}
                    >
                      First name (primary contact)
                    </label>
                    <input
                      id={`ip-${uid}-first`}
                      type="text"
                      className={`ip-${uid}-finput ${showErr("firstName") ? `ip-${uid}-finput-err` : ""}`}
                      value={draft.firstName}
                      onChange={updateField("firstName")}
                      onBlur={markTouched("firstName")}
                      autoFocus
                    />
                    {showErr("firstName") && (
                      <p className={`ip-${uid}-ferr`}>{errors.firstName}</p>
                    )}
                  </div>
                  <div className={`ip-${uid}-field`}>
                    <label
                      htmlFor={`ip-${uid}-last`}
                      className={`ip-${uid}-flabel`}
                    >
                      Last name (primary contact)
                    </label>
                    <input
                      id={`ip-${uid}-last`}
                      type="text"
                      className={`ip-${uid}-finput ${showErr("lastName") ? `ip-${uid}-finput-err` : ""}`}
                      value={draft.lastName}
                      onChange={updateField("lastName")}
                      onBlur={markTouched("lastName")}
                    />
                    {showErr("lastName") && (
                      <p className={`ip-${uid}-ferr`}>{errors.lastName}</p>
                    )}
                  </div>
                  <div className={`ip-${uid}-field`}>
                    <label
                      htmlFor={`ip-${uid}-email`}
                      className={`ip-${uid}-flabel`}
                    >
                      Email
                    </label>
                    <input
                      id={`ip-${uid}-email`}
                      type="email"
                      className={`ip-${uid}-finput ${showErr("email") ? `ip-${uid}-finput-err` : ""}`}
                      value={draft.email}
                      onChange={updateField("email")}
                      onBlur={markTouched("email")}
                    />
                    {showErr("email") && (
                      <p className={`ip-${uid}-ferr`}>{errors.email}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>

        {showFooter && (
          <footer className={`ip-${uid}-foot`}>
            <button
              type="button"
              className={`ip-${uid}-save ${saveDisabled ? `ip-${uid}-save-disabled` : ""}`}
              onClick={() => {
                if (saveDisabled) return;
                const form = document.querySelector(
                  `.ip-${uid}-body`,
                ) as HTMLFormElement | null;
                form?.requestSubmit();
              }}
              disabled={saveDisabled}
              title={
                saveDisabled
                  ? "Firm ID invites launch with Novala for Accountants"
                  : undefined
              }
            >
              Save
            </button>
          </footer>
        )}
      </div>
    </>
  );
}

function useInvitePageStyles(uid: string) {
  const css = `
    .ip-${uid}-page {
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      background: #ffffff;
      width: 100%;
    }
    .ip-${uid}-top {
      position: sticky;
      top: 0;
      z-index: 10;
      border-bottom: 1px solid ${PALETTE.line};
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 32px;
      background: #ffffff;
      flex-shrink: 0;
      gap: 16px;
    }
    .ip-${uid}-top-stack {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }
    .ip-${uid}-crumb {
      font-size: 12px;
      color: ${PALETTE.ink3};
      font-weight: 500;
      margin: 0;
    }
    .ip-${uid}-crumb-link {
      color: ${PALETTE.ink3};
      text-decoration: none;
      cursor: pointer;
    }
    .ip-${uid}-crumb-link:hover {
      text-decoration: underline;
      color: ${PALETTE.ink2};
    }
    .ip-${uid}-crumb-sep {
      margin: 0 6px;
      color: ${PALETTE.ink4};
    }
    .ip-${uid}-crumb-current {
      color: ${PALETTE.ink};
      font-weight: 600;
    }
    .ip-${uid}-top-title {
      font-size: 18px;
      font-weight: 700;
      color: ${PALETTE.ink};
      letter-spacing: -0.012em;
      margin: 0;
    }
    .ip-${uid}-close {
      background: transparent;
      border: 0;
      padding: 8px;
      border-radius: 8px;
      color: ${PALETTE.ink};
      cursor: pointer;
      font-family: inherit;
      flex-shrink: 0;
    }
    .ip-${uid}-close:hover { background: ${PALETTE.lineSoft}; }

    .ip-${uid}-body {
      flex: 1;
      padding: 36px 36px 32px;
      max-width: 820px;
      width: 100%;
      margin: 0 auto;
      display: flex;
      flex-direction: column;
    }
    .ip-${uid}-sub {
      font-size: 20px;
      font-weight: 700;
      color: ${PALETTE.ink};
      margin: 0 0 10px;
      letter-spacing: -0.012em;
    }
    .ip-${uid}-intro {
      font-size: 13.5px;
      color: ${PALETTE.ink2};
      margin: 0 0 28px;
      line-height: 1.6;
      max-width: 640px;
    }

    .ip-${uid}-ropt { margin-bottom: 20px; }
    .ip-${uid}-rhead {
      display: flex;
      align-items: center;
      gap: 11px;
      cursor: pointer;
      user-select: none;
    }
    .ip-${uid}-radio {
      width: 20px;
      height: 20px;
      accent-color: ${PALETTE.teal};
      cursor: pointer;
      flex-shrink: 0;
    }
    .ip-${uid}-rlabel {
      font-size: 14px;
      color: ${PALETTE.ink};
      font-weight: 500;
    }

    .ip-${uid}-rbody { padding: 14px 0 0 31px; }
    .ip-${uid}-row3 {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 14px;
    }
    .ip-${uid}-field-single { max-width: 460px; }
    .ip-${uid}-field { display: flex; flex-direction: column; gap: 6px; }
    .ip-${uid}-flabel {
      font-size: 12.5px;
      font-weight: 700;
      color: ${PALETTE.ink};
      margin: 0 0 6px;
      display: block;
    }
    .ip-${uid}-finput {
      width: 100%;
      padding: 10px 14px;
      border: 1.5px solid ${PALETTE.ink4};
      border-radius: 7px;
      font-size: 13.5px;
      color: ${PALETTE.ink};
      background: #ffffff;
      font-family: inherit;
      transition: border-color 160ms, box-shadow 160ms;
    }
    .ip-${uid}-finput:focus {
      outline: 0;
      border-color: ${PALETTE.ink};
      box-shadow: 0 0 0 3px rgba(14, 26, 26, 0.08);
    }
    .ip-${uid}-finput-err { border-color: ${PALETTE.red}; }
    .ip-${uid}-finput-err:focus {
      box-shadow: 0 0 0 3px rgba(185, 28, 28, 0.1);
    }
    .ip-${uid}-ferr {
      font-size: 12px;
      color: ${PALETTE.red};
      margin: 4px 0 0;
      font-weight: 500;
    }
    .ip-${uid}-explain {
      background: ${PALETTE.tealBg};
      border: 1px solid #CFE0D9;
      border-radius: 8px;
      padding: 11px 14px;
      margin: 10px 0 0;
      font-size: 12.5px;
      color: ${PALETTE.ink};
      line-height: 1.55;
    }
    .ip-${uid}-explain strong {
      color: ${PALETTE.tealDark};
      font-weight: 700;
    }
    .ip-${uid}-explain code {
      background: #ffffff;
      padding: 1px 5px;
      border-radius: 3px;
      font-size: 11.5px;
      font-family: ui-monospace, Menlo, monospace;
      color: ${PALETTE.tealDark};
    }
    .ip-${uid}-note {
      font-size: 12px;
      color: ${PALETTE.ink3};
      margin: 10px 0 0;
      line-height: 1.55;
    }

    .ip-${uid}-foot {
      background: ${PALETTE.ink};
      padding: 14px 32px;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      flex-shrink: 0;
      position: sticky;
      bottom: 0;
      z-index: 10;
      animation: ip-${uid}-slide-up 240ms ease-out;
    }
    @keyframes ip-${uid}-slide-up {
      from { transform: translateY(100%); }
      to { transform: translateY(0); }
    }
    @media (prefers-reduced-motion: reduce) {
      .ip-${uid}-foot { animation: none; }
    }
    .ip-${uid}-save {
      background: ${PALETTE.teal};
      color: #ffffff;
      border: 0;
      padding: 10px 32px;
      border-radius: 7px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      transition: background 160ms, opacity 160ms;
    }
    .ip-${uid}-save:hover { background: ${PALETTE.tealDark}; }
    .ip-${uid}-save-disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    .ip-${uid}-save-disabled:hover { background: ${PALETTE.teal}; }

    @media (max-width: 720px) {
      .ip-${uid}-top { padding: 12px 20px; }
      .ip-${uid}-body { padding: 24px 20px 24px; }
      .ip-${uid}-foot { padding: 12px 20px; }
      .ip-${uid}-row3 { grid-template-columns: 1fr; }
      .ip-${uid}-rbody { padding-left: 0; }
    }
  `;
  return <style>{css}</style>;
}